/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Injectable } from '@angular/core';
import * as _ from 'underscore';
import { AuditService } from '@app/common/audit.service';

@Injectable({
  providedIn: 'root'
})
export class NPRAuditService {
  constructor(private auditService: AuditService) {}

  // Create Report Events
  logCreateReportSuccessEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Create,
      this.auditService.getActionType().ReportCreation,
      this.auditService.getEventOutcome().Success,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  logCreateReportOnLegacySuccessEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Create,
      this.auditService.getActionType().ReportCreationOnLegacy,
      this.auditService.getEventOutcome().Success,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  logCreateReportFailureEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Create,
      this.auditService.getActionType().ReportCreationFailure,
      this.auditService.getEventOutcome().SeriousFailure,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  logCreateReportOnLegacyFailureEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Create,
      this.auditService.getActionType().ReportCreationOnLegacyFailure,
      this.auditService.getEventOutcome().SeriousFailure,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  // Save Report Events
  logSaveReportSuccessEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Execute,
      this.auditService.getActionType().ReportSave,
      this.auditService.getEventOutcome().Success,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  logSaveReportFailureEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Execute,
      this.auditService.getActionType().ReportSaveFailure,
      this.auditService.getEventOutcome().SeriousFailure,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  //Report Delete Events
  logDeleteReportSuccessEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Delete,
      this.auditService.getActionType().ReportDeletion,
      this.auditService.getEventOutcome().Success,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  logDeleteReportFailureEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Delete,
      this.auditService.getActionType().ReportDeletionFailure,
      this.auditService.getEventOutcome().SeriousFailure,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  //Report Update Events
  logUpdateReportSuccessEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Update,
      this.auditService.getActionType().ReportUpdation,
      this.auditService.getEventOutcome().Success,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  logUpdateReportFailureEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Update,
      this.auditService.getActionType().ReportUpdationFailure,
      this.auditService.getEventOutcome().MajorFailure,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  //Report Discard Events
  logDiscardReportSuccessEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Delete,
      this.auditService.getActionType().ReportDiscard,
      this.auditService.getEventOutcome().Success,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  //Report Emergency Discard Events
  logEmergencyDiscardReportSuccessEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Delete,
      this.auditService.getActionType().ReportEmergencyDiscard,
      this.auditService.getEventOutcome().Success,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  logEmergencyDiscardReportFailureEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Delete,
      this.auditService.getActionType().ReportEmergencyDiscardFailure,
      this.auditService.getEventOutcome().SeriousFailure,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  //Report Addemdum Delete Events
  logReportAddendumDeleteSuccessEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Delete,
      this.auditService.getActionType().AddendumDeletion,
      this.auditService.getEventOutcome().Success,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }

  logReportAddendumDeleteFailureEvent(workingItem, participantName, reason?) {
    this.auditService.saveReportAuditAction(
      this.auditService.getActionCode().Delete,
      this.auditService.getActionType().AddendumDeletionFailure,
      this.auditService.getEventOutcome().MajorFailure,
      workingItem.reportId,
      participantName,
      workingItem.status,
      reason,
      workingItem.auditData
    );
  }
}
